/**
 * ALERON grid density.
 *
 * Desktop 2 / 4 / 6 columns, mobile 1 / 2 / 3. The choice is stored in
 * localStorage so it survives navigation between collection pages.
 *
 * The stored value is applied in two places:
 *   - here, on connect, for normal navigation and for grids that arrive from
 *     the Section Rendering API after a filter change;
 *   - in a tiny inline script in the section (see aleron-collection.liquid),
 *     which runs before first paint so the grid never flashes at the default
 *     density and then re-lays out.
 *
 * Column count is applied as a custom property, so changing density is one
 * property write and never touches the DOM of the cards themselves.
 */

const STORAGE_KEY = 'aleron:grid-density';

/** Column options offered per viewport, in the order they appear. */
export const DENSITIES = {
  mobile: [1, 2, 3],
  desktop: [2, 4, 6]
};

/** @returns {{mobile: number|null, desktop: number|null}} */
export function readDensity() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mobile: null, desktop: null };

    const parsed = JSON.parse(raw);
    return {
      mobile: DENSITIES.mobile.includes(parsed.mobile) ? parsed.mobile : null,
      desktop: DENSITIES.desktop.includes(parsed.desktop) ? parsed.desktop : null
    };
  } catch {
    // Private mode, disabled storage, or a value written by an older build.
    return { mobile: null, desktop: null };
  }
}

function writeDensity(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Not being able to remember the choice is not a reason to fail applying it.
  }
}

class AleronGrid extends HTMLElement {
  connectedCallback() {
    this.stored = readDensity();
    this.#apply();

    this.addEventListener('click', this.#handleClick);

    // Another collection page in a second tab should not fight this one, but
    // a change made in this tab's own storage should still be picked up.
    window.addEventListener('storage', this.#handleStorage);

    // Filtering re-renders the collection section through Horizon's morph,
    // which updates this element in place: it is never reconnected, so
    // connectedCallback does not run again, while the grid's style and the
    // toggles' aria-pressed are overwritten with the server's defaults. A
    // shopper who chose 6 per row saw 4 again after every filter. Re-assert
    // the stored choice whenever the subtree changes. #apply only writes a
    // value that differs, so its own writes settle instead of looping.
    this.#observer = new MutationObserver(() => this.#apply());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'data-columns', 'aria-pressed']
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    window.removeEventListener('storage', this.#handleStorage);
    this.#observer?.disconnect();
  }

  #observer = null;

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-aleron-density]') : null;
    if (!button) return;

    const viewport = button.dataset.viewport;
    const columns = Number(button.dataset.aleronDensity);
    if (!DENSITIES[viewport]?.includes(columns)) return;

    this.stored = { ...this.stored, [viewport]: columns };
    writeDensity(this.stored);
    this.#apply();
  };

  #handleStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;
    this.stored = readDensity();
    this.#apply();
  };

  #apply() {
    // Re-queried every time: a re-render can replace the list outright, or
    // remove it entirely when a filter returns no products.
    const grid = this.querySelector('[data-aleron-grid]');
    if (!grid) return;

    const mobile = this.stored.mobile ?? (Number(grid.dataset.defaultMobile) || 2);
    const desktop = this.stored.desktop ?? (Number(grid.dataset.defaultDesktop) || 4);

    // Write only on a real difference, so the MutationObserver above does not
    // re-trigger itself forever.
    const set = (prop, value) => {
      if (grid.style.getPropertyValue(prop).trim() !== value) grid.style.setProperty(prop, value);
    };
    set('--aleron-columns-mobile', String(mobile));
    set('--aleron-columns-desktop', String(desktop));

    // Read by the CSS that loosens the card crop at one column.
    if (grid.dataset.columns !== String(mobile)) grid.dataset.columns = String(mobile);

    for (const button of this.querySelectorAll('[data-aleron-density]')) {
      const current = button.dataset.viewport === 'mobile' ? mobile : desktop;
      const pressed = String(Number(button.dataset.aleronDensity) === current);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    }
  }
}

if (!customElements.get('aleron-grid')) customElements.define('aleron-grid', AleronGrid);
